import React, { useEffect, useState } from 'react';
import {
    Card,
    Table,
    Badge,
    Typography,
    Row,
    Col,
    Statistic,
    Tag,
    Collapse,
    Space,
    Divider,
    Empty,
    Tooltip
} from 'antd';
import {
    TeamOutlined,
    UserOutlined,
    UserAddOutlined
} from '@ant-design/icons';
import { DepartmentEmpServices } from '../../../Services/Manage/DepartmentEmpServices';
import { UserService } from '../../../Services/Manage/UserService';

const { Title, Text } = Typography;
const { Panel } = Collapse;

const ViewByDepartment = () => {
    const [dptList, setDptList] = useState([]);
    const [userList, setUserList] = useState([]);

    const getAllDepartment = async () => {
        try {
            const request = await DepartmentEmpServices.getAllDepartment();
            setDptList(request.data.sort((a, b) => new Date(a.added_on) - new Date(b.added_on)));
        } catch (error) {
            console.error(error);
        }
    };

    const getAllUser = async () => {
        try {
            const request = await UserService.getAllUsers();
            setUserList(request.data);
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        getAllDepartment();
        getAllUser();
    }, []);

    // Group users by department
    const getDepartmentEmployees = (departmentId) => {
        return userList.filter(user => user.department_id === departmentId);
    };

    // Get department statistics
    const getDepartmentStats = (departmentId) => {
        const employees = getDepartmentEmployees(departmentId);
        const activeEmployees = employees.filter(emp => emp.status === 'Active').length;
        const inactiveEmployees = employees.filter(emp => emp.status !== 'Active').length;

        return {
            total: employees.length,
            active: activeEmployees,
            inactive: inactiveEmployees
        };
    };

    // Format date
    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const employeeColumns = [
        {
            title: "SI No",
            dataIndex: "key",
            align: 'center',
            key: "key",
            render: (_, __, index) => index + 1,
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render: (text, record) => (
                <Space>
                    <Text strong>{text}</Text>
                    <Text type="secondary">({record.user_id})</Text>
                </Space>
            ),
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
        },
        {
            title: 'Phone',
            dataIndex: 'phone',
            key: 'phone',
        },
        {
            title: 'Role',
            dataIndex: 'role',
            key: 'role',
            render: role => <Tag color="blue">{role}</Tag>,
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: status => (
                <Tag style={{ fontSize: '16px', padding: '6px 12px' }} color={status === 'Active' ? 'green' : 'red'}>
                    {status}
                </Tag>
            ),
        },
        {
            title: 'Added On',
            dataIndex: 'added_on',
            key: 'added_on',
            render: formatDate,
        },
    ];

    return (
        <div>
            {/* Department Statistics Summary */}
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col span={6}>
                    <Card>
                        <Statistic
                            title="Total Departments"
                            value={dptList.length}
                            prefix={<TeamOutlined />}
                            valueStyle={{ color: '#1890ff' }}
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card>
                        <Statistic
                            title="Active Departments"
                            value={dptList.filter(dept => dept.status === 'Active').length}
                            valueStyle={{ color: '#52c41a' }}
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card>
                        <Statistic
                            title="Total Employees"
                            value={userList.length}
                            prefix={<UserOutlined />}
                            valueStyle={{ color: '#722ed1' }}
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card>
                        <Statistic
                            title="Active Employees"
                            value={userList.filter(user => user.status === 'Active').length}
                            valueStyle={{ color: '#52c41a' }}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Department Details */}
            <Collapse
                defaultActiveKey={dptList.map(dept => dept.id.toString())}
                size="large"
            >
                {dptList.map(department => {
                    const stats = getDepartmentStats(department.id);
                    const employees = getDepartmentEmployees(department.id);

                    return (
                        <Panel
                            key={department.id}
                            header={
                                <Row justify="space-between" align="middle" style={{ width: '100%', marginRight: 16 }}>
                                    <Col>
                                        <Space>
                                            <TeamOutlined style={{ fontSize: 16, color: '#1890ff' }} />
                                            <Text strong style={{ fontSize: 16 }}>
                                                {department.name}
                                            </Text>

                                            <Tag color={department.status === 'Active' ? 'cyan' : 'volcano'}>
                                                {department.status}
                                            </Tag>
                                        </Space>
                                    </Col>
                                    <Col>
                                        <Space>
                                            <Tooltip title="Active Employees">
                                                <Badge
                                                    count={
                                                        <span
                                                            style={{
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                fontWeight: "bold",
                                                                color: "white",
                                                                width: "100%",
                                                                height: "100%",
                                                            }}
                                                        >
                                                            {stats.active}
                                                        </span>
                                                    }
                                                    style={{
                                                        backgroundColor: "#2a4b19ff",
                                                        minWidth: 28,
                                                        height: 28,
                                                        lineHeight: "28px",
                                                        borderRadius: "50%",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                    }}
                                                />


                                            </Tooltip>
                                            {stats.inactive > 0 && (
                                                <Tooltip title="Inactive Employees">
                                                    <Badge
                                                        count={stats.inactive}
                                                        style={{ backgroundColor: '#f5222d' }}
                                                    />
                                                </Tooltip>
                                            )}
                                        </Space>
                                    </Col>
                                </Row>
                            }
                        >
                            <Row gutter={[16, 16]}>
                                <Col span={24}>
                                    <Card size="small" style={{ backgroundColor: '#fafafa' }}>
                                        <Row gutter={16}>
                                            <Col span={12}>
                                                <Text strong>Description: </Text>
                                                <Text>{department.description}</Text>
                                            </Col>
                                            <Col span={6}>
                                                <Text strong>Created: </Text>
                                                <Text>{formatDate(department.added_on)}</Text>
                                            </Col>
                                            <Col span={6}>
                                                <Text strong>Last Updated: </Text>
                                                <Text>{formatDate(department.updated_on)}</Text>
                                            </Col>
                                        </Row>
                                    </Card>
                                </Col>

                                <Col span={24}>

                                    {employees.length > 0 ? (
                                        <Table
                                            columns={employeeColumns}
                                            dataSource={employees}
                                            rowKey="user_id"
                                            pagination={false}
                                            size="small"
                                            style={{ marginTop: 16 }}
                                        />
                                    ) : (
                                        <Empty
                                            description="No employees in this department"
                                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                                        />
                                    )}
                                </Col>
                            </Row>
                        </Panel>
                    );
                })}
            </Collapse>
        </div>
    );
};

export default ViewByDepartment;